import axios from "axios";
import {
  getStoredToken,
  isTokenExpired,
  clearAuthAndRedirectToLogin,
} from "../utils/auth";

const WABA_API_BASE = process.env.REACT_APP_WABA_SERVER_URL;

const chatAPI = axios.create({
  baseURL: `${WABA_API_BASE}/api/chat`,
  headers: { "Content-Type": "application/json" },
});

chatAPI.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (!token || isTokenExpired(token)) {
    clearAuthAndRedirectToLogin();
    return Promise.reject(new Error("Token expired"));
  }
  config.headers.Authorization = `Bearer ${token}`;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.user_id) {
      config.headers["X-User-Data"] = JSON.stringify({ id: payload.user_id });
    }
  } catch {
    // continue without X-User-Data
  }
  return config;
});

chatAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthAndRedirectToLogin();
    }
    return Promise.reject(error);
  }
);

/**
 * Get messages for a specific conversation
 */
export const getMessages = async (customerPhone, phoneNumberId, limit = 50, offset = 0) => {
  if (!customerPhone || !phoneNumberId) {
    throw new Error("customerPhone and phoneNumberId are required");
  }
  return chatAPI.get("/messages", {
    params: {
      customer_phone: String(customerPhone),
      phone_number_id: String(phoneNumberId),
      limit,
      offset,
      _ts: Date.now(), // bust browser cache — signed media URLs must be fresh
    },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });
};

/**
 * Send a text message
 * @param {string|null} replyToMessageId - Optional WhatsApp message_id to quote-reply
 */
export const sendMessage = async (phoneNumberId, customerPhone, messageText, replyToMessageId = null) => {
  return chatAPI.post("/send", {
    phone_number_id: phoneNumberId,
    customer_phone: customerPhone,
    message_text: messageText,
    reply_to_message_id: replyToMessageId || null,
  });
};

/**
 * Send a media message
 * @param {string|null} replyToMessageId - Optional WhatsApp message_id to quote-reply
 */
export const sendMediaMessage = async (phoneNumberId, customerPhone, mediaUrl, mediaType, caption = null, replyToMessageId = null) => {
  return chatAPI.post("/send", {
    phone_number_id: phoneNumberId,
    customer_phone: customerPhone,
    media_url: mediaUrl,
    media_type: mediaType,
    message_text: caption,
    reply_to_message_id: replyToMessageId || null,
  });
};

/**
 * Send a WhatsApp template message to a customer in live chat.
 */
export const sendTemplateMessage = async (
  phoneNumberId,
  customerPhone,
  template,
  userVars
) => {
  return chatAPI.post("/send-template", {
    phone_number_id: phoneNumberId,
    customer_phone: customerPhone,
    template,
    userVars,
  });
};

/**
 * Check if conversation window is open (24-hour rule)
 */
export const checkWindowStatus = async (customerPhone, phoneNumberId) => {
  return chatAPI.get("/window-status", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Download media from Meta and store in object storage (on-demand)
 */
export const downloadMedia = async (messageId, phoneNumberId) => {
  return chatAPI.post("/download-media", {
    message_id: messageId,
    phone_number_id: phoneNumberId,
  });
};

/**
 * Browser-safe media URL for chat bubbles.
 * Proxies private Saksham objects through wabaserver so production Ceph
 * InvalidArgument / AccessDenied on signed URLs never hits the browser.
 */
export function getChatMediaSrc(message, phoneNumberId) {
  if (!message) return null;
  const phoneId = phoneNumberId || message.phone_number_id;
  const stored = message.downloaded_media_url || message.media_url;
  if (!stored) return null;

  const isPrivateStore =
    /object\.sakshamcloud\.com|speraza-marketplace-bucket|\/(whatsapp-media|chat-media|business-docs|downloads)\//i.test(
      stored
    ) || Boolean(message.downloaded_media_url);

  if (!isPrivateStore || !message.message_id || !phoneId) {
    return message.downloaded_media_url || message.media_url;
  }

  const token = getStoredToken();
  if (!token || !WABA_API_BASE) {
    return message.downloaded_media_url || message.media_url;
  }

  const canonical =
    message.canonical_url ||
    (typeof stored === "string" && stored.includes("?")
      ? stored.slice(0, stored.indexOf("?"))
      : stored);

  const params = new URLSearchParams({
    phone_number_id: String(phoneId),
    token: String(token),
    object: String(canonical),
    _v: String(message.downloaded_media_url || message.media_url || "").length,
  });
  return `${WABA_API_BASE}/api/chat/media-file/${encodeURIComponent(
    message.message_id
  )}?${params.toString()}`;
}

/**
 * Upload file for chat media messages
 * @param {File} file - File object from input
 * @returns {Promise} - Response with media_url and media_type
 */
export const uploadChatMedia = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        const base64 = reader.result;
        const response = await chatAPI.post("/upload-media", {
          file: base64,
          fileName: file.name,
          contentType: file.type,
        });
        resolve(response);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Mark messages as read for a conversation
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @returns {Promise} - Response with marked count
 */
export const markMessagesAsRead = async (customerPhone, phoneNumberId) => {
  return chatAPI.post("/mark-read", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
  });
};

/**
 * Get AI status for a specific chat
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @returns {Promise} - Response with ai_enabled status
 */
export const getChatAiStatus = async (customerPhone, phoneNumberId) => {
  return chatAPI.get("/ai-status", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Toggle AI status for a specific chat
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @param {boolean} aiEnabled - Whether AI should be enabled
 * @returns {Promise} - Response with updated ai_enabled status
 */
export const toggleChatAiStatus = async (customerPhone, phoneNumberId, aiEnabled) => {
  return chatAPI.post("/toggle-ai", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
    ai_enabled: aiEnabled,
  });
};

/**
 * Get agent flow control status for a chat
 */
export const getFlowControlStatus = async (customerPhone, phoneNumberId) => {
  return chatAPI.get("/flow-control-status", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Agent takes control — pauses automated flow for this chat
 */
export const takeFlowControl = async (customerPhone, phoneNumberId) => {
  return chatAPI.post("/take-flow-control", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
  });
};

/**
 * Agent returns control — resumes flow from the previous step
 */
export const releaseFlowControl = async (customerPhone, phoneNumberId) => {
  return chatAPI.post("/release-flow-control", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
  });
};

/**
 * Get engaged customers with scores and filters
 * @param {Object} filters - Filter options
 * @param {string} filters.phone_number_id - Optional phone number ID
 * @param {number} filters.min_score - Minimum total score
 * @param {number} filters.max_score - Maximum total score
 * @param {string} filters.start_date - Start date (YYYY-MM-DD)
 * @param {string} filters.end_date - End date (YYYY-MM-DD)
 * @param {string} filters.sort_by - Sort by "total_score" or "avg_score"
 * @param {string} filters.sort_order - Sort order "asc" or "desc"
 * @param {number} filters.limit - Number of results
 * @param {number} filters.offset - Offset for pagination
 * @returns {Promise} - Response with engaged customers
 */
export const getEngagedCustomers = async (filters = {}) => {
  return chatAPI.get("/engaged-customers", { params: filters });
};

/**
 * Export all engaged customers chats with scores
 * @param {Object} filters - Filter options (same as getEngagedCustomers)
 * @returns {Promise} - Response with all engaged customers data
 */
export const exportAllEngagedCustomers = async (filters = {}) => {
  return chatAPI.get("/engaged-customers/export-all", { params: filters });
};

/**
 * Export engaged customer chat with scores
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @returns {Promise} - Response with messages and engagement scores
 */
export const exportEngagedCustomerChat = async (customerPhone, phoneNumberId) => {
  return chatAPI.get("/engaged-customers/export", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Generate chat summary using ChatGPT
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @param {number} messageCount - Number of last messages to include (default: 50)
 * @returns {Promise} - Response with structured summary
 */
export const generateChatSummary = async (customerPhone, phoneNumberId, messageCount = 50) => {
  return chatAPI.post("/generate-summary", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
    message_count: messageCount,
  });
};

export default chatAPI;

